import { FC, useCallback, useMemo } from "react";
import { useRouter } from "next/router";
import gql from "graphql-tag";
import { toast } from "sonner";

import { useUpdateOrgFeatureFlagsMutation } from "@src/features/app/top-nav/SetOrgFeatureFlags.generated";
import { track } from "@src/features/app/utils/track";
import {
  useFeatureFlag,
  useFeatureFlagsLoaded,
} from "@src/features/app/hooks/useFeatureFlag";
import useUser from "@src/features/app/hooks/useUser";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import { useBooleanLocalStorage } from "@src/lib/hooks/useLocalStorage";
import { useToast } from "@src/lib/hooks/useToast";
import { Button } from "@src/lib/ui/Button";
import Pill from "@src/lib/ui/Pill";
import Text from "@src/lib/ui/Text";
import { launchConfetti } from "@src/lib/utils/confetti";
import { Ff, GraphqlOperations, UserType } from "@src/types.generated";
import { usePathsOptIn_FeatureFlagsQuery } from "./PathsOptIn.generated";

const PathsOptIn: FC = () => {
  const { pathname } = useRouter();
  const { user, isStaff } = useUser();
  const { userActionPermissions } = useActionPermissions();
  const flagsLoaded = useFeatureFlagsLoaded();
  const enabled = useFeatureFlag(Ff.PathsAsWorkflows);
  // `enabled` alone can't tell us whether *this* user is allowed to flip
  // the flag; the server refuses a non-staff admin unless the flag is
  // marked self-servable, and refuses it as a GraphQL error.
  const onPathsRoute = pathname.startsWith("/path");
  const { data: flagMeta } = usePathsOptIn_FeatureFlagsQuery({
    skip: !onPathsRoute,
  });
  const selfServable = useMemo(
    () =>
      flagMeta?.FeatureFlags.some(
        (f) => f.name === Ff.PathsAsWorkflows && f.adminSelfServable,
      ) ?? false,
    [flagMeta],
  );
  const [dismissed, setDismissed] = useBooleanLocalStorage(
    "dismissed_paths_opt_in",
    false,
  );
  const [updateOrgFeatureFlags, { loading }] = useUpdateOrgFeatureFlagsMutation(
    {
      refetchQueries: [
        GraphqlOperations.Query.FeatureFlags,
        GraphqlOperations.Query.OrgFeatureFlags,
      ],
    },
  );
  const { addErrorToast } = useToast();
  const showOptInError = useCallback(
    () =>
      addErrorToast({
        message: "Couldn't turn on the new paths builder",
        callsite: "paths_opt_in",
      }),
    [addErrorToast],
  );

  const handleTry = useCallback(() => {
    updateOrgFeatureFlags({
      variables: {
        input: {
          featureFlags: [
            {
              name: Ff.PathsAsWorkflows,
              enabled: { value: true },
            },
          ],
        },
      },
      onCompleted: (data) => {
        if (!data.updateOrgFeatureFlags.success) {
          showOptInError();
          return;
        }
        launchConfetti();
        toast.success("The new paths builder is on for your org");
        track("feature-opt-in", {
          feature: "New Paths",
          enabled: true,
        });
      },
      onError: showOptInError,
    });
  }, [updateOrgFeatureFlags, showOptInError]);

  if (
    !onPathsRoute ||
    !flagsLoaded ||
    enabled ||
    dismissed ||
    user?.userType !== UserType.Admin ||
    // With the flag on, /paths and its nav item both require these. An
    // admin whose role drops either would turn the page off for the org
    // and lose their own way back to it.
    !userActionPermissions?.manageLibrary ||
    !userActionPermissions?.updateAutomations ||
    !(selfServable || isStaff)
  ) {
    return null;
  }

  return (
    <div
      data-testid="paths-opt-in"
      className="flex w-[220px] flex-col rounded-2xl border border-border-primary bg-bg-primary p-3 shadow-default"
    >
      <div className="flex self-stretch pb-1">
        <Pill title="New" color="purple" />
      </div>
      <Text variant="compact-title" className="pb-1 text-text-primary">
        New paths builder
      </Text>
      <Text
        variant="fine-print-title"
        multiline
        className="text-text-secondary"
      >
        A drag-and-drop canvas, versions, and per-person runs — redesigned.
      </Text>
      <Button
        size="sm"
        className="mt-3 self-stretch"
        onClick={handleTry}
        loading={loading}
      >
        Turn on for your org
      </Button>
      <button
        type="button"
        className="mt-2 cursor-pointer self-end text-text-tertiary hover:text-text-secondary"
        onClick={() => setDismissed(true)}
      >
        <Text variant="fine-print-title">Dismiss</Text>
      </button>
    </div>
  );
};

gql`
  query PathsOptIn_FeatureFlags {
    FeatureFlags {
      name
      adminSelfServable
    }
  }
`;

export default PathsOptIn;
